import React, { useState } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';
const getToken = () => localStorage.getItem('token') || '';

interface HostConfig {
  idub: string;
  uMark: string;
  uName: string;
  uCont: string;
}

interface TestGuestRow {
  firstName: string;
  lastName: string;
  birthYear: number;
  stateCode: string;
  idNumber: string;
  checkInDate: string;
  checkOutDate: string;
  visaNumber: string;
}

interface TestResultRow {
  index: number;
  name: string;
  success: boolean;
  reference: string | null;
  headerError: string | null;
  recordErrors: string[];
  errorMessage: string | null;
}

const FORMAT = 'firstName;lastName;birthYear;stateCode;documentNumber;checkInDate;checkOutDate;visaNumber(opt)';
const EXAMPLE = 'Hans;Mueller;1978;DEU;C01X00T47;2026-05-13;2026-05-16;\nBukayo;Saka;2005;VNM;VN1663938;2026-05-13;2026-05-16;17363897';

function parseCsv(text: string): { rows: TestGuestRow[]; errors: string[] } {
  const rows: TestGuestRow[] = [];
  const errors: string[] = [];
  const lines = text.split('\n');
  let lineNum = 0;
  for (const raw of lines) {
    lineNum++;
    const line = raw.trim();
    if (!line || line.startsWith('#') || line.toLowerCase().startsWith('firstname')) continue;
    const p = line.split(';');
    if (p.length < 7) {
      errors.push(`Line ${lineNum}: need ≥7 fields, got ${p.length}`);
      continue;
    }
    const birthYear = parseInt(p[2], 10);
    if (isNaN(birthYear) || birthYear < 1900 || birthYear > 2020) {
      errors.push(`Line ${lineNum}: invalid birthYear "${p[2]}" (expected 1900–2020)`);
      continue;
    }
    const checkIn = p[5].trim();
    const checkOut = p[6].trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(checkIn) || !/^\d{4}-\d{2}-\d{2}$/.test(checkOut)) {
      errors.push(`Line ${lineNum}: dates must be yyyy-MM-dd`);
      continue;
    }
    rows.push({
      firstName: p[0].trim(),
      lastName: p[1].trim(),
      birthYear,
      stateCode: p[3].trim(),
      idNumber: p[4].trim(),
      checkInDate: checkIn,
      checkOutDate: checkOut,
      visaNumber: p[7]?.trim() ?? '',
    });
  }
  return { rows, errors };
}

const inputStyle: React.CSSProperties = {
  padding: '6px 10px', borderRadius: 5, border: '1px solid #e2e8f0',
  fontSize: '0.875rem', width: '100%', boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: '0.78rem', fontWeight: 600, color: '#4a5568', marginBottom: 3, display: 'block',
};

const AdminUbyportTest: React.FC = () => {
  const [host, setHost] = useState<HostConfig>({ idub: '', uMark: '', uName: '', uCont: '' });
  const [delayMin, setDelayMin] = useState(0);
  const [delayMax, setDelayMax] = useState(3000);
  const [csvText, setCsvText] = useState('');
  const [parsedGuests, setParsedGuests] = useState<TestGuestRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [results, setResults] = useState<TestResultRow[]>([]);
  const [sending, setSending] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [done, setDone] = useState(false);

  const setHostField = (field: keyof HostConfig) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setHost(prev => ({ ...prev, [field]: e.target.value }));

  const handleParse = () => {
    const { rows, errors } = parseCsv(csvText);
    setParsedGuests(rows);
    setParseErrors(errors);
    setResults([]);
    setDone(false);
  };

  const handleSend = async () => {
    if (parsedGuests.length === 0 || sending) return;
    setSending(true);
    setResults([]);
    setDone(false);

    const lo = Math.max(0, delayMin);
    const hi = Math.max(lo, delayMax);

    for (let i = 0; i < parsedGuests.length; i++) {
      const g = parsedGuests[i];
      setCurrentIndex(i + 1);
      try {
        const res = await axios.post(`${API_BASE}/admin/ubyport/test-guest`, {
          ...g,
          idub: host.idub || null,
          uMark: host.uMark || null,
          uName: host.uName || null,
          uCont: host.uCont || null,
        }, {
          headers: { Authorization: `Bearer ${getToken()}` },
          withCredentials: true,
        });
        const d = res.data;
        setResults(prev => [...prev, {
          index: i + 1,
          name: `${g.firstName} ${g.lastName}`,
          success: d.success,
          reference: d.reference ?? null,
          headerError: d.headerError ?? null,
          recordErrors: d.recordErrors ?? [],
          errorMessage: d.errorMessage ?? null,
        }]);
      } catch (err: any) {
        setResults(prev => [...prev, {
          index: i + 1,
          name: `${g.firstName} ${g.lastName}`,
          success: false,
          reference: null,
          headerError: null,
          recordErrors: [],
          errorMessage: err.response?.data?.message || err.message || 'Network error',
        }]);
      }
      if (i < parsedGuests.length - 1 && hi > 0) {
        const delay = lo === hi ? lo : lo + Math.floor(Math.random() * (hi - lo + 1));
        await new Promise<void>(resolve => setTimeout(resolve, delay));
      }
    }

    setSending(false);
    setDone(true);
  };

  const successCount = results.filter(r => r.success).length;

  return (
    <div>
      <h2 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 6, color: '#2d3748' }}>Ubyport Batch Test</h2>
      <p style={{ color: '#718096', marginBottom: 16, fontSize: '0.9rem' }}>
        Configure host details, paste guest rows, and send one-by-one. No DB writes — pure SOAP test.
      </p>

      {/* Host config */}
      <div style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#2d3748', marginBottom: 12 }}>Host / Apartment Config</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
          <div>
            <label style={labelStyle}>IDUB (uIdub)</label>
            <input style={inputStyle} value={host.idub} onChange={setHostField('idub')} placeholder="2086201258877" disabled={sending} />
          </div>
          <div>
            <label style={labelStyle}>Apartment short name (uMark, max 10)</label>
            <input style={inputStyle} value={host.uMark} onChange={setHostField('uMark')} placeholder="TWS208" maxLength={10} disabled={sending} />
          </div>
          <div>
            <label style={labelStyle}>Host name (uName, max 30)</label>
            <input style={inputStyle} value={host.uName} onChange={setHostField('uName')} placeholder="Duc Vuong Tran" maxLength={30} disabled={sending} />
          </div>
          <div>
            <label style={labelStyle}>Host phone (uCont)</label>
            <input style={inputStyle} value={host.uCont} onChange={setHostField('uCont')} placeholder="777111609" disabled={sending} />
          </div>
        </div>
      </div>

      {/* Delay config */}
      <div style={{ background: '#f7fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <div style={{ fontWeight: 700, fontSize: '0.875rem', color: '#2d3748', marginBottom: 12 }}>Delay Between Submissions</div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={labelStyle}>Min (ms)</label>
            <input
              type="number" min={0} max={10000} step={100}
              style={{ ...inputStyle, width: 100 }}
              value={delayMin}
              onChange={e => setDelayMin(Math.max(0, parseInt(e.target.value) || 0))}
              disabled={sending}
            />
          </div>
          <div>
            <label style={labelStyle}>Max (ms)</label>
            <input
              type="number" min={0} max={10000} step={100}
              style={{ ...inputStyle, width: 100 }}
              value={delayMax}
              onChange={e => setDelayMax(Math.max(0, parseInt(e.target.value) || 0))}
              disabled={sending}
            />
          </div>
          <div style={{ fontSize: '0.8rem', color: '#718096', paddingBottom: 2 }}>
            {delayMin === delayMax
              ? `Fixed ${delayMin} ms`
              : `Random ${delayMin}–${delayMax} ms per guest`}
          </div>
        </div>
      </div>

      {/* CSV input */}
      <div style={{ fontFamily: 'monospace', fontSize: '0.78rem', color: '#4a5568', background: '#edf2f7', padding: '6px 10px', borderRadius: 4, marginBottom: 6 }}>
        {FORMAT}
      </div>
      <textarea
        value={csvText}
        onChange={e => { setCsvText(e.target.value); setParsedGuests([]); setResults([]); setDone(false); }}
        placeholder={EXAMPLE}
        rows={10}
        style={{
          width: '100%', fontFamily: 'monospace', fontSize: '0.85rem',
          padding: 10, borderRadius: 6, border: '1px solid #e2e8f0',
          resize: 'vertical', boxSizing: 'border-box',
        }}
        disabled={sending}
      />

      <div style={{ display: 'flex', gap: 10, marginTop: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          onClick={handleParse}
          disabled={!csvText.trim() || sending}
          style={{ padding: '8px 20px', background: '#3182ce', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
        >
          Parse
        </button>
        <button
          onClick={handleSend}
          disabled={parsedGuests.length === 0 || sending}
          style={{ padding: '8px 20px', background: '#38a169', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
        >
          {sending ? `Sending ${currentIndex} / ${parsedGuests.length}…` : `Send ${parsedGuests.length} guest(s)`}
        </button>
        {parsedGuests.length > 0 && !sending && !done && (
          <span style={{ color: '#718096', fontSize: '0.875rem' }}>{parsedGuests.length} rows parsed, ready to send</span>
        )}
        {done && (
          <span style={{ fontWeight: 700, color: successCount === parsedGuests.length ? '#38a169' : '#c53030' }}>
            Done — {successCount} / {parsedGuests.length} successful
          </span>
        )}
      </div>

      {parseErrors.length > 0 && (
        <div style={{ marginTop: 10, background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 6, padding: '8px 12px' }}>
          {parseErrors.map((e, i) => (
            <div key={i} style={{ color: '#c53030', fontSize: '0.85rem' }}>{e}</div>
          ))}
        </div>
      )}

      {results.length > 0 && (
        <div style={{ marginTop: 20, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{ background: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>#</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Name</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Reference (stamp)</th>
                <th style={{ padding: '8px 10px', textAlign: 'left' }}>Errors</th>
              </tr>
            </thead>
            <tbody>
              {results.map(r => (
                <tr key={r.index} style={{ borderBottom: '1px solid #e2e8f0', background: r.success ? '#f0fff4' : '#fff5f5' }}>
                  <td style={{ padding: '8px 10px', color: '#718096' }}>{r.index}</td>
                  <td style={{ padding: '8px 10px', fontWeight: 500 }}>{r.name}</td>
                  <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                    <span style={{ color: r.success ? '#38a169' : '#c53030', fontWeight: 700 }}>
                      {r.success ? '✓ OK' : '✗ FAILED'}
                    </span>
                    {r.headerError && (
                      <span style={{ marginLeft: 6, color: '#d69e2e', fontSize: '0.78rem' }}>⚠ header warning</span>
                    )}
                  </td>
                  <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: '0.8rem', color: '#4a5568' }}>
                    {r.reference ?? '—'}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: '0.8rem', color: '#c53030' }}>
                    {r.errorMessage && <div>{r.errorMessage}</div>}
                    {r.headerError && <div style={{ color: '#d69e2e' }}>Header: {r.headerError}</div>}
                    {r.recordErrors.map((e, i) => <div key={i}>{e}</div>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminUbyportTest;
